/**
 * Music ducking under voice.
 *
 * Fast in, slow out, scheduled from the source start/end times rather than
 * timers, so the duck is sample-accurate (AC.9).
 */

/**
 * 6 dB, down from 9. The voice speaks for most of every slot, so the duck is
 * the level the music is usually heard at; at 9 dB even a full music slider
 * sat far under the narration.
 */
export const DUCK_DB = -6;
export const DUCK_IN_SEC = 0.12;
export const DUCK_OUT_SEC = 0.8;

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

export const DUCK_GAIN = dbToGain(DUCK_DB);

/**
 * Schedule a duck on `musicDuck` at context time `when`, for `durationSec`.
 * Returns the time at which recovery completes.
 */
export function scheduleDuck(
  musicDuck: AudioParam,
  when: number,
  durationSec: number,
  options: { duckGain?: number; inSec?: number; outSec?: number } = {},
): number {
  const duck = options.duckGain ?? DUCK_GAIN;
  const inSec = options.inSec ?? DUCK_IN_SEC;
  const outSec = options.outSec ?? DUCK_OUT_SEC;
  const start = Math.max(0, when);
  const end = start + Math.max(0, durationSec);

  musicDuck.cancelScheduledValues(start);
  musicDuck.setValueAtTime(1, start);
  musicDuck.linearRampToValueAtTime(duck, start + inSec);
  musicDuck.setValueAtTime(duck, Math.max(start + inSec, end));
  musicDuck.linearRampToValueAtTime(1, Math.max(start + inSec, end) + outSec);
  return Math.max(start + inSec, end) + outSec;
}

/** Music-only bed level while a non-duckable narrator speaks. */
export const FIXED_BED_GAIN = dbToGain(-6);
